import { motion } from 'framer-motion';
import { Github, Linkedin, Twitter } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useLocation, useNavigate, Link } from 'react-router-dom';

const socialLinks = [
  { name: 'Twitter', icon: Twitter, href: '#' },
  { name: 'LinkedIn', icon: Linkedin, href: '#' },
  { name: 'GitHub', icon: Github, href: '#' },
];

export const Footer = () => {
  const { t } = useLanguage();
  const data = t('footer') || { product: 'Product', company: 'Company', copyright: '© 2026 Doora.', tagline: 'AI Enterprise Platform', description: 'Enterprise AI that actually works.' };
  const location = useLocation();
  const navigate = useNavigate();

  const footerLinks = {
    product: [
      { name: t('nav.whyDoora') || 'Why Doora', href: '#features' },
      { name: t('nav.product') || 'Product', href: '/product' },
      { name: t('nav.research') || 'Research', href: '/research' },
      { name: t('nav.getDemo') || 'Get Demo', href: '#demo' },
    ],
    legal: [
      { name: data.termsOfService, href: '/terms' },
      { name: data.privacyPolicy, href: '/privacy' },
      { name: data.security, href: '/security' },
    ],
  };

  const handleNavClick = (e, href) => {
    e.preventDefault();
    if (href.startsWith('/')) {
      navigate(href);
      return;
    }

    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <footer data-testid="footer" className="relative border-t border-zinc-800 bg-zinc-950 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 pb-0">
        {/* Main Footer Content */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-10">
          {/* Logo & Description */}
          <div className="col-span-2">
            <Link
              to="/"
              className="flex items-center gap-2 mb-4"
              onClick={(e) => {
                if (location.pathname === '/') {
                  e.preventDefault();
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }}
            >
            <span className="text-xl sm:text-2xl font-extrabold tracking-wide leading-loose text-zinc-100 gradient-text">
              Doora
            </span>
            </Link>
            <p className="text-md text-zinc-400 leading-relaxed mb-4 max-w-xs">
              {data.description}
            </p>
            {/* Social Links */}
            <div className="flex gap-2">
              {socialLinks.map((social) => {
                const Icon = social.icon;
                return (
                  <a
                    key={social.name}
                    href={social.href}
                    data-testid={`footer-social-${social.name.toLowerCase()}`}
                    className="w-8 h-8 rounded-lg bg-zinc-900 border border-zinc-800 flex items-center justify-center text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 transition-colors"
                  >
                    <Icon className="w-4 h-4" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Product Links */}
          <div>
            <h4 className="text-sm font-semibold text-zinc-100 mb-4">{data.product}</h4>
            <ul className="space-y-2.5">
              {footerLinks.product.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    onClick={(e) => handleNavClick(e, link.href)}
                    className="text-sm text-zinc-400 hover:text-zinc-100 transition-colors cursor-pointer"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="text-sm font-semibold text-zinc-100 mb-4">{data.company}</h4>
            <ul className="space-y-2.5">
              <li><a href="#" className="text-sm text-zinc-400 hover:text-zinc-100 transition-colors">{data.about}</a></li>
              <li><a href="#" className="text-sm text-zinc-400 hover:text-zinc-100 transition-colors">{data.blog}</a></li>
              <li><a href="#" className="text-sm text-zinc-400 hover:text-zinc-100 transition-colors">{data.careers}</a></li>
              <li><a href="#demo" onClick={(e) => handleNavClick(e, '#demo')} className="text-sm text-zinc-400 hover:text-zinc-100 transition-colors cursor-pointer">{data.contact}</a></li>
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h4 className="text-sm font-semibold text-zinc-100 mb-4">{data.legal}</h4>
            <ul className="space-y-2.5">
              {footerLinks.legal.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    onClick={(e) => handleNavClick(e, link.href)}
                    className="text-sm text-zinc-400 hover:text-zinc-100 transition-colors cursor-pointer"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-3 relative z-10">
          <p className="text-sm text-zinc-400">
            {data.copyright}
          </p>
          <p className="text-xs text-zinc-400">
            {data.tagline}
          </p>
        </div>

        {/* Big Doora Text with Animated Circular Gradient Background - Grayscale for B&W theme */}
        <div className="mt-3 text-center relative pointer-events-none" style={{ marginBottom: '-3.2rem' }}>
          {/* White Doora Text */}
          <span
            className="relative text-7xl sm:text-8xl md:text-9xl lg:text-[16rem] xl:text-[24rem] font-bold select-none leading-none opacity-70 shimmer-text"
          >
            Doora
          </span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
